import React, { useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { useGetProfileQuery } from "../store/trader-api";
import { openFormSidebar, closeFormSidebar } from "../store/slices/uiSlice";
import HoldersTable from "../components/HoldersTable";

const RequisitesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background: var(--bg-primary);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const AddButton = styled.button<{ isCancel?: boolean }>`
  background: ${(props) => (props.isCancel ? "var(--text-secondary)" : "var(--accent-primary)")};
  color: #ffffff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${(props) => (props.isCancel ? "var(--text-primary)" : "var(--accent-primary-hover)")};
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const FiltersContainer = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  margin-bottom: 1.5rem;
`;

const FiltersTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: var(--bg-card);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--bg-card);
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
`;

const PaginationInfo = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  background: ${(props) => (props.active ? "var(--accent-primary)" : "var(--bg-card)")};
  color: ${(props) => (props.active ? "#ffffff" : "var(--text-primary)")};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.active ? "var(--accent-primary-hover)" : "var(--bg-hover)")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NewRequisites: React.FC = () => {
  const dispatch = useDispatch();
  const { formSidebarOpen } = useSelector((state: RootState) => state.ui);
  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 20,
    search: "",
    tag: "",
    bankId: "",
    currency: "",
    // VP-205: countryId более не относится к держателю — фильтр убран.
    status: "",
  });

  // Получаем профиль трейдера
  const { data: profile } = useGetProfileQuery();
  const traderId = profile?.id;

  const handleToggleForm = () => {
    if (formSidebarOpen) {
      dispatch(closeFormSidebar());
    } else {
      dispatch(openFormSidebar());
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setSearchParams((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Сбрасываем на первую страницу при изменении фильтров
    }));
  };

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({
      ...prev,
      page,
    }));
  };

  if (!traderId) {
    return (
      <RequisitesContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          Загрузка профиля...
        </div>
      </RequisitesContainer>
    );
  }

  return (
    <RequisitesContainer>
      <Header>
        <Title>Реквизиты</Title>
        <HeaderActions>
          <AddButton isCancel={formSidebarOpen} onClick={handleToggleForm}>
            <span>{formSidebarOpen ? "×" : "+"}</span>
            {formSidebarOpen ? "Отмена" : "Добавить реквизит"}
          </AddButton>
        </HeaderActions>
      </Header>

      {/* Фильтры */}
      <FiltersContainer>
        <FiltersTitle>Фильтры и поиск</FiltersTitle>
        <FiltersGrid>
          <FilterGroup>
            <FilterLabel>Поиск</FilterLabel>
            <FilterInput
              type="text"
              placeholder="Поиск по имени, названию..."
              value={searchParams.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
            />
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Название</FilterLabel>
            <FilterInput
              type="text"
              placeholder="Фильтр по названию"
              value={searchParams.tag}
              onChange={(e) => handleFilterChange("tag", e.target.value)}
            />
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Валюта</FilterLabel>
            <FilterSelect
              value={searchParams.currency}
              onChange={(e) => handleFilterChange("currency", e.target.value)}
            >
              <option value="">Все валюты</option>
              <option value="RUB">Российский рубль</option>
              <option value="USD">Доллар США</option>
              <option value="EUR">Евро</option>
              <option value="KZT">Казахстанский тенге</option>
            </FilterSelect>
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>Статус</FilterLabel>
            <FilterSelect
              value={searchParams.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">Все статусы</option>
              <option value="true">Активные</option>
              <option value="false">Неактивные</option>
            </FilterSelect>
          </FilterGroup>
        </FiltersGrid>
      </FiltersContainer>

      {/* Таблица держателей */}
      <HoldersTable traderId={traderId} searchParams={searchParams} />
    </RequisitesContainer>
  );
};

export default NewRequisites;
